import React, { useState, useEffect } from "react";
import { useAllSettings } from "@/hooks/queries/useSettings";
import {
  useCreateSetting,
  useUpdateSetting,
  useDeleteSetting,
} from "@/hooks/mutations/settings";
import { useQueryClient } from "@tanstack/react-query";
import { Edit, Delete, Trash2Icon, Plus, Save } from "lucide-react";
import useToastStore from "@/components/Common/Toast/toastStore";
import "react-toastify/dist/ReactToastify.css";
import Loader from "@/components/Loader";
import DialogBox from "../../components/Common/DialogBox";

const SettingsList = () => {
  const queryClient = useQueryClient();
  const { data: settings, isLoading, isError } = useAllSettings();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState(null);
  const [currentSetting, setCurrentSetting] = useState(null);
  const [newFields, setNewFields] = useState({});
  const [newSettingFields, setNewSettingFields] = useState([
    { key: "", value: "" },
  ]);
  const [localEdits, setLocalEdits] = useState({});
  const [saveButtons, setSaveButtons] = useState({});
  const { mutate: createSetting } = useCreateSetting();
  const { mutate: updateSetting } = useUpdateSetting();
  const { mutate: deleteSetting } = useDeleteSetting();
  const { showToast } = useToastStore;
  useEffect(() => {
    if (isDialogOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [isDialogOpen]);
  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading settings..." />
      </div>
    );
  }

  const settingsData = Array.isArray(settings?.data) ? settings?.data : [];

  const handleSave = () => {
    const settingData = {
      label: document.getElementById("setting-label").value,
      key: document.getElementById("setting-key").value,
      value: newSettingFields.reduce((acc, field) => {
        if (field.key) {
          try {
            acc[field.key] = JSON.parse(field.value);
          } catch (e) {
            acc[field.key] = field.value;
          }
        }
        return acc;
      }, {}),
    };

    if (currentSetting) {
      updateSetting(
        { id: currentSetting.key, settingData },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(["settings"]);
            setCurrentSetting(null);
            setIsDialogOpen(false);
            setNewSettingFields([{ key: "", value: "" }]);
            showToast({
              message: "Setting updated successfully!",
              type: "success",
            });
          },
          onError: (error) => {
            showToast({
              message:
                error.response?.data?.message || "Failed to update setting.",
              type: "error",
            });
          },
        },
      );
    } else {
      createSetting(settingData, {
        onSuccess: () => {
          queryClient.invalidateQueries(["settings"]);
          setIsDialogOpen(false);
          setNewSettingFields([{ key: "", value: "" }]);
          showToast({
            message: "Setting added successfully!",
            type: "success",
          });
        },
        onError: (error) => {
          showToast({
            message: error.response?.data?.message || "Failed to add setting.",
            type: "error",
          });
        },
      });
    }
  };

  const handleDelete = (key) => {
    setSettingToDelete(key);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!settingToDelete) return;

    deleteSetting(settingToDelete, {
      onSuccess: () => {
        queryClient.invalidateQueries(["settings"]);
        showToast({
          message: "Setting deleted successfully!",
          type: "success",
        });
        setIsDeleteDialogOpen(false);
        setSettingToDelete(null);
      },
      onError: () => {
        showToast({
          message: "Failed to delete setting.",
          type: "error",
        });
      },
    });
  };

  const handleAddField = (settingKey) => {
    setNewFields((prev) => ({
      ...prev,
      [settingKey]: { key: "", value: "" },
    }));
  };

  const handleSaveNewField = (settingKey) => {
    const newField = newFields[settingKey];
    if (!newField?.key) return;

    const settingToUpdate = settingsData.find((s) => s.key === settingKey);
    if (!settingToUpdate) return;

    const updatedValue = {
      ...(typeof settingToUpdate.value === "object"
        ? settingToUpdate.value
        : {}),
      [newField.key]: newField.value,
    };

    updateSetting(
      {
        id: settingKey,
        settingData: {
          ...settingToUpdate,
          value: updatedValue,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["settings"]);
          setNewFields((prev) => {
            const newState = { ...prev };
            delete newState[settingKey];
            return newState;
          });
          showToast({
            message: "New field saved successfully!",
            type: "success",
          });
        },
        onError: (error) => {
          showToast({
            message:
              error.response?.data?.message || "Failed to save new field.",
            type: "error",
          });
        },
      },
    );
  };

  const handleDeleteField = (settingKey, fieldKey) => {
    const settingToUpdate = settingsData.find((s) => s.key === settingKey);
    if (!settingToUpdate || !settingToUpdate.value[fieldKey]) return;

    const updatedValue = { ...settingToUpdate.value };
    delete updatedValue[fieldKey];

    updateSetting(
      {
        id: settingKey,
        settingData: {
          ...settingToUpdate,
          value: updatedValue,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["settings"]);
          showToast({
            message: "Field deleted successfully!",
            type: "success",
          });
        },
        onError: (error) => {
          showToast({
            message: error.response?.data?.message || "Failed to delete field.",
            type: "error",
          });
        },
      },
    );
  };

  const handleFieldChange = (settingKey, fieldKey, newValue) => {
    const settingToUpdate = settingsData.find((s) => s.key === settingKey);
    if (!settingToUpdate) return;

    // Update local edits first
    setLocalEdits((prev) => {
      const updatedSetting = {
        ...prev[settingKey],
        [fieldKey]: newValue,
      };

      // Check if there are any changes compared to original setting
      const originalFields = settingToUpdate.value;
      const hasAnyChanges = Object.keys(updatedSetting).some(
        (key) => updatedSetting[key] !== originalFields[key],
      );

      // Update save button visibility accordingly
      setSaveButtons((prevButtons) => ({
        ...prevButtons,
        [settingKey]: hasAnyChanges,
      }));

      return {
        ...prev,
        [settingKey]: updatedSetting,
      };
    });
  };

  const handleSaveFieldChanges = (settingKey) => {
    const settingToUpdate = settingsData.find((s) => s.key === settingKey);
    if (!settingToUpdate || !localEdits[settingKey]) return;

    // Calculate checksum: Compare the current edits with the original value
    const hasChanges = Object.keys(localEdits[settingKey]).some((key) => {
      return localEdits[settingKey][key] !== settingToUpdate.value[key];
    });

    if (!hasChanges) {
      showToast({
        message: "No changes detected. Nothing to save.",
        type: "info",
      });
      return;
    }

    const updatedValue = {
      ...settingToUpdate.value,
      ...localEdits[settingKey],
    };

    updateSetting(
      {
        id: settingKey,
        settingData: {
          ...settingToUpdate,
          value: updatedValue,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["settings"]);
          setLocalEdits((prev) => {
            const newState = { ...prev };
            delete newState[settingKey];
            return newState;
          });
          setSaveButtons((prev) => ({
            ...prev,
            [settingKey]: false,
          }));
          showToast({
            message: "Changes saved successfully!",
            type: "success",
          });
        },
        onError: (error) => {
          showToast({
            message: error.response?.data?.message || "Failed to save changes.",
            type: "error",
          });
        },
      },
    );
  };

  const handleDiscardChanges = (settingKey) => {
    // Remove local edits for this setting
    setLocalEdits((prev) => {
      const updatedEdits = { ...prev };
      delete updatedEdits[settingKey];
      return updatedEdits;
    });

    // Hide the save button for this setting
    setSaveButtons((prev) => ({
      ...prev,
      [settingKey]: false,
    }));

    // Reset the input fields to their original values
    const settingToUpdate = settingsData.find((s) => s.key === settingKey);
    if (settingToUpdate) {
      const originalFields = settingToUpdate.value;
      Object.keys(originalFields).forEach((key) => {
        const inputElement = document.querySelector(
          `textarea[name="${settingKey}-${key}"]`,
        );
        if (inputElement) {
          inputElement.value =
            typeof originalFields[key] === "object"
              ? JSON.stringify(originalFields[key])
              : originalFields[key];
        }
      });
    }
  };

  const handleAddNewSettingField = () => {
    setNewSettingFields([...newSettingFields, { key: "", value: "" }]);
  };

  const handleRemoveNewSettingField = (index) => {
    const updatedFields = [...newSettingFields];
    updatedFields.splice(index, 1);
    setNewSettingFields(updatedFields);
  };

  const handleNewSettingFieldChange = (index, field, value) => {
    const updatedFields = [...newSettingFields];
    updatedFields[index][field] = value;
    setNewSettingFields(updatedFields);
  };

  const handleCancelNewField = (settingKey) => {
    setNewFields((prev) => {
      const newState = { ...prev };
      delete newState[settingKey];
      return newState;
    });
  };

  const handleNewFieldChange = (settingKey, field, value) => {
    setNewFields((prev) => ({
      ...prev,
      [settingKey]: {
        ...prev[settingKey],
        [field]: value,
      },
    }));
  };

  const handleEditSetting = (setting) => {
    setCurrentSetting(setting);
    if (setting.value && typeof setting.value === "object") {
      const fields = Object.entries(setting.value).map(([key, value]) => ({
        key,
        value:
          typeof value === "object" ? JSON.stringify(value, null, 2) : value,
      }));
      setNewSettingFields(fields);
    } else {
      setNewSettingFields([{ key: "", value: "" }]);
    }
    setIsDialogOpen(true);
  };

  const renderObjectFields = (setting) => {
    return (
      <div className="bg-muted p-2 rounded-md text-sm space-y-1">
        {Object.entries(setting.value).map(([key, value]) => (
          <div key={key} className="flex gap-2 items-center group">
            <span className="font-medium w-1/4">{key}:</span>
            <textarea
              name={`${setting.key}-${key}`}
              rows={2}
              defaultValue={
                typeof value === "object" ? JSON.stringify(value) : value
              }
              onChange={(e) =>
                handleFieldChange(setting.key, key, e.target.value)
              }
              className="border border-input rounded-md px-2 py-1 focus:outline-none focus:border-green-500 w-3/4"
            />
            <button
              onClick={() => handleDeleteField(setting.key, key)}
              className="text-red-500 hover:text-red-700 invisible group-hover:visible"
            >
              <Trash2Icon className="w-4 h-4" />
            </button>
          </div>
        ))}

        {newFields[setting.key] && (
          <div className="bg-muted p-3 rounded-md border border-border mt-2">
            <div className="flex gap-2 items-center mb-3">
              <span className="font-medium w-1/4 text-muted-foreground">New Key:</span>
              <input
                type="text"
                value={newFields[setting.key].key || ""}
                onChange={(e) =>
                  handleNewFieldChange(setting.key, "key", e.target.value)
                }
                className="border border-input rounded-md px-3 py-1.5 focus:outline-none focus:border-green-500 w-3/4 text-sm"
                placeholder="Enter field key"
              />
            </div>
            <div className="flex gap-2 items-center mb-3">
              <span className="font-medium w-1/4 text-muted-foreground">
                New Value:
              </span>
              <textarea
                rows={3}
                value={newFields[setting.key].value || ""}
                onChange={(e) =>
                  handleNewFieldChange(setting.key, "value", e.target.value)
                }
                className="border border-input rounded-md px-3 py-1.5 focus:outline-none focus:border-green-500 w-3/4 text-sm"
                placeholder="Enter field value"
              />
            </div>
            <div className="flex justify-end gap-3 mt-2">
              <button
                onClick={() => handleCancelNewField(setting.key)}
                className="px-4 py-1.5 text-sm font-medium text-foreground bg-muted rounded-md hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveNewField(setting.key)}
                className="px-4 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark transition-colors"
              >
                Save Field
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          {!newFields[setting.key] && (
            <button
              onClick={() => handleAddField(setting.key)}
              className="flex items-center gap-1 bg-primary-dark text-white px-3 py-1 rounded-md hover:bg-primary-dark transition-colors text-sm"
            >
              Add Field
            </button>
          )}

          {saveButtons[setting.key] && (
            // <button
            //     onClick={() => handleSaveFieldChanges(setting.key)}
            //     className="flex items-center gap-1 bg-primary text-white px-3 py-1 rounded-md hover:bg-primary-dark transition-colors text-sm"
            // >
            //     Save Changes
            // </button>

            <>
              <button
                onClick={() => handleSaveFieldChanges(setting.key)}
                className="flex items-center gap-1 bg-primary text-white px-3 py-1 rounded-md hover:bg-primary-dark transition-colors text-sm"
              >
                Save Changes
              </button>
              <button
                onClick={() => handleDiscardChanges(setting.key)}
                className="flex items-center gap-1 bg-muted-foreground text-white px-3 py-1 rounded-md hover:bg-muted-foreground transition-colors text-sm"
              >
                Discard Changes
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12  px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Settings
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Configure global system parameters, manage metadata, and track
                application configurations.
              </p>
            </div>
            <button
              onClick={() => {
                setCurrentSetting(null);
                setNewSettingFields([{ key: "", value: "" }]);
                setIsDialogOpen(true);
              }}
              className="bg-primary-dark text-white px-4 prounded-lg h-[36px] shadow-sm font-semibold transition-all hover:bg-primary-dark"
              id="add-setting-btn"
            >
              Add Setting
            </button>
          </div>
        </div>

        <div className="overflow-x-auto bg-card rounded-lg shadow border border-border">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider w-1/5">
                  Label & Key
                </th>
                <th className="px-6 py-5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider w-4/5">
                  Configuration Value
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {settingsData.map((setting) => (
                <tr
                  key={setting.key}
                  className="hover:bg-muted transition-colors duration-200"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                    <div className="text-sm font-semibold text-foreground">
                      {setting.label}
                    </div>
                    <div className="text-xs text-primary-dark mt-1">
                      {setting.key}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-normal break-words text-sm text-muted-foreground">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        {Array.isArray(setting.value) ? (
                          setting.value.map((item, index) => (
                            <div key={index}>
                              {typeof item === "object" ? (
                                <div className="bg-muted p-2 rounded-md text-sm space-y-1 group">
                                  {Object.entries(item).map(([key, value]) => (
                                    <div
                                      key={key}
                                      className="flex gap-2 items-center"
                                    >
                                      <span className="font-medium w-1/4">
                                        {key}:
                                      </span>
                                      <textarea
                                        rows={2}
                                        defaultValue={
                                          typeof value === "object"
                                            ? JSON.stringify(value)
                                            : value
                                        }
                                        onChange={(e) =>
                                          handleFieldChange(
                                            setting.key,
                                            key,
                                            e.target.value,
                                          )
                                        }
                                        className="border border-input rounded-md px-2 py-1 focus:outline-none focus:border-green-500"
                                      />
                                      <button
                                        onClick={() =>
                                          handleDeleteField(setting.key, key)
                                        }
                                        className="text-red-500 hover:text-red-700 hidden group-hover:block"
                                      >
                                        <Trash2Icon className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <textarea
                                  rows={2}
                                  defaultValue={item}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      setting.key,
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  className="border border-input rounded-md px-2 py-1 focus:outline-none focus:border-green-500 w-full"
                                />
                              )}
                            </div>
                          ))
                        ) : typeof setting.value === "object" ? (
                          renderObjectFields(setting)
                        ) : (
                          <textarea
                            rows={2}
                            defaultValue={setting.value}
                            onChange={(e) =>
                              handleFieldChange(
                                setting.key,
                                "value",
                                e.target.value,
                              )
                            }
                            className="border border-input rounded-md px-2 py-1 focus:outline-none focus:border-green-500 w-full"
                          />
                        )}
                      </div>

                      <div className="flex-shrink-0 pt-1">
                        <button
                          onClick={() => handleDelete(setting.key)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          title="Delete Setting"
                        >
                          <Trash2Icon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogBox
          isOpen={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setNewSettingFields([{ key: "", value: "" }]);
          }}
          title={currentSetting ? "Edit Setting" : "Add Setting"}
          className="w-full max-w-2xl"
          actionButton={
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-dark hover:bg-primary-dark rounded-md transition-colors"
            >
              Save Setting
            </button>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Setting Label
              </label>
              <input
                id="setting-label"
                defaultValue={currentSetting?.label}
                onChange={(e) => {
                  const labelValue = e.target.value;
                  const keyValue = labelValue
                    .replace(/\s+/g, "_")
                    .toLowerCase();
                  document.getElementById("setting-key").value = keyValue;
                }}
                className="mt-1 block w-full rounded-md border border-input px-3 py-2 shadow-sm focus:border-green-500 focus:outline-none focus:ring-green-500"
                placeholder="Enter a label"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Setting Key
              </label>
              <input
                id="setting-key"
                defaultValue={currentSetting?.key}
                className="mt-1 block w-full rounded-md border border-input px-3 py-2 shadow-sm focus:border-green-500 focus:outline-none focus:ring-green-500"
                placeholder="Enter a key"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Setting Value (JSON Object)
              </label>
              <div className="space-y-3">
                {newSettingFields.map((field, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Key
                      </label>
                      <input
                        value={field.key}
                        onChange={(e) =>
                          handleNewSettingFieldChange(
                            index,
                            "key",
                            e.target.value,
                          )
                        }
                        className="block w-full rounded-md border border-input px-3 py-2 shadow-sm focus:border-green-500 focus:outline-none focus:ring-green-500 text-sm"
                        placeholder="Enter a key"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Value
                      </label>
                      <textarea
                        value={field.value}
                        onChange={(e) =>
                          handleNewSettingFieldChange(
                            index,
                            "value",
                            e.target.value,
                          )
                        }
                        className="block w-full rounded-md border border-input px-3 py-2 shadow-sm focus:border-green-500 focus:outline-none focus:ring-green-500 text-sm"
                        rows={3}
                        placeholder="Enter a value"
                      />
                    </div>
                    <div className="pt-6">
                      <button
                        type="button"
                        onClick={() => handleRemoveNewSettingField(index)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2Icon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={handleAddNewSettingField}
                  className="flex items-center text-sm text-green-700 hover:text-green-900 mt-2 font-medium"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Field
                </button>
              </div>

              <div className="mt-4 bg-muted p-3 rounded-md border border-border">
                <h4 className="text-sm font-medium text-foreground mb-2">
                  Preview
                </h4>
                <pre className="text-xs bg-card p-2 rounded border border-border overflow-x-auto">
                  {JSON.stringify(
                    newSettingFields.reduce((acc, field) => {
                      if (field.key) {
                        try {
                          acc[field.key] = JSON.parse(field.value);
                        } catch (e) {
                          acc[field.key] = field.value;
                        }
                      }
                      return acc;
                    }, {}),
                    null,
                    2,
                  )}
                </pre>
              </div>
            </div>
          </div>
        </DialogBox>

        <DialogBox
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setSettingToDelete(null);
          }}
          title="Delete Setting"
          className="w-[40%] max-md:w-[60%]"
          actionButton={
            <button
              type="button"
              onClick={confirmDelete}
              className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto transition-colors"
            >
              Delete
            </button>
          }
        >
          <div className="text-muted-foreground text-sm mt-1 max-w-[390px]">
            Are you sure you want to delete this setting? This action cannot be
            undone.
          </div>
        </DialogBox>
      </div>
    </div>
  );
};

export default SettingsList;
